import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Fuel, Clock, ShieldCheck, Truck, Gauge, FileCheck2, MapPin, PhoneCall, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const HERO_IMG = "https://images.pexels.com/photos/5410923/pexels-photo-5410923.jpeg";
const BOWSER_SIDE = "https://customer-assets.emergentagent.com/job_fuel-on-demand-23/artifacts/ztgeo1uz_Leaflets-05_2_optimized.jpg";
const BOWSER_COLLAGE = "https://customer-assets.emergentagent.com/job_fuel-on-demand-23/artifacts/vjjskt9q_TANKER_PHOTOS_5000_2_optimized_2000.jpg";
const FLYER_ILLUSTRATION = "https://customer-assets.emergentagent.com/job_fuel-on-demand-23/artifacts/jpk7901n_Flyer-01_3_optimized.jpg";
const LEAFLET_ILLUSTRATION = "https://customer-assets.emergentagent.com/job_fuel-on-demand-23/artifacts/68xqu0ch_Leaflets-01_2_optimized.jpg";

export default function Home() {
    const [pricing, setPricing] = useState(null);
    useEffect(() => {
        api.get("/pricing").then(r => setPricing(r.data)).catch(() => {});
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative overflow-hidden bg-slate-900 text-white" data-testid="hero-section">
                <div className="absolute inset-0">
                    <img src={HERO_IMG} alt="fuel truck" className="w-full h-full object-cover opacity-40" />
                    <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/70 to-slate-900/40"></div>
                    <div className="absolute inset-0 hero-grid"></div>
                </div>

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-28 lg:pt-28 lg:pb-40">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 border border-yellow-400/40 bg-yellow-400/10 backdrop-blur-sm px-3 py-1.5 rounded-sm mb-8 fade-up" data-testid="hero-badge">
                            <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></span>
                            <span className="text-xs font-mono-plex uppercase tracking-widest text-yellow-100">Authorised BPCL Dealer · Vadodara &amp; Central Gujarat</span>
                        </div>
                        <h1 className="font-heading font-black text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95] fade-up">
                            Diesel, delivered<br />
                            <span className="text-yellow-400">to your doorstep.</span>
                        </h1>
                        <p className="mt-6 text-lg md:text-xl text-slate-300 max-w-2xl leading-relaxed fade-up">
                            Authorised Bharat Petroleum (BPCL) dealer supplying on-time, metered, high-quality High-Speed Diesel to factories, farms, gensets & construction sites — from 250 litres upward.
                        </p>

                        <div className="mt-10 flex flex-wrap items-center gap-4 fade-up">
                            <Link to="/order">
                                <Button className="rounded-sm bg-blue-700 hover:bg-blue-800 text-white h-12 px-8 text-base font-bold uppercase tracking-wider transition-transform hover:-translate-y-0.5" data-testid="hero-cta-order">
                                    Order Diesel Now <ArrowRight className="ml-2 w-4 h-4" />
                                </Button>
                            </Link>
                            <a href="tel:+919512477777" className="inline-flex items-center gap-2 text-white h-12 px-6 border border-slate-600 hover:bg-slate-800 rounded-sm text-sm font-bold uppercase tracking-wider" data-testid="hero-cta-call">
                                <PhoneCall className="w-4 h-4" /> Call For Bulk
                            </a>
                        </div>

                        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-slate-700 pt-8 fade-up">
                            {[
                                { k: "250L", v: "Minimum Order" },
                                { k: "4hr", v: "Delivery ETA" },
                                { k: "24×7", v: "Order Window" },
                                { k: "TAX", v: "Compliant Invoice" },
                            ].map((s) => (
                                <div key={s.v}>
                                    <div className="font-heading font-black text-3xl md:text-4xl text-yellow-400 tab-num">{s.k}</div>
                                    <div className="text-xs uppercase tracking-widest text-slate-400 mt-1 font-mono-plex">{s.v}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* MARQUEE / TRUSTED */}
            <section className="bg-yellow-400 border-y border-yellow-500 overflow-hidden">
                <div className="flex whitespace-nowrap marquee-track py-4">
                    {Array(2).fill(0).map((_, i) => (
                        <div key={i} className="flex items-center gap-12 shrink-0 px-6 text-blue-900 font-heading font-bold uppercase tracking-widest text-sm">
                            <span>· Factories</span><span>· Warehouses</span><span>· Construction Sites</span><span>· Diesel Gensets</span><span>· Transport Fleets</span><span>· Farms &amp; Agri</span><span>· Real Estate</span><span>· Hospitals</span><span>· Data Centers</span><span>· Cold Storage</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* WHY US */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="why-us-section">
                <div className="grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4">
                        <div className="text-xs font-mono-plex uppercase tracking-[0.2em] text-blue-700 mb-4">// Why Vaishali Fuels</div>
                        <h2 className="font-heading font-black text-4xl md:text-5xl tracking-tighter text-slate-900 leading-tight">
                            Built for industrial reliability.
                        </h2>
                        <p className="mt-6 text-slate-600 leading-relaxed">
                            We are not a fuel aggregator. We operate our own bowsers, our own calibrated meters, and our own trained crew — because a stopped generator or an idle excavator costs you far more than a litre of diesel.
                        </p>
                    </div>
                    <div className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
                        {[
                            { icon: Clock, title: "On-Time, Every Time", body: "Delivery windows honoured within 4 hours of confirmation. Live status updates from dispatch to drop-off." },
                            { icon: Gauge, title: "Metered & Calibrated", body: "Weights & Measures certified flow meters. You pay for exactly what is dispensed — down to the last drop." },
                            { icon: ShieldCheck, title: "PESO-Compliant Safety", body: "Trained operators, fire-safe bowsers, PPE, ESD protocols. Zero-incident record maintained since day one." },
                            { icon: FileCheck2, title: "Tax-Compliant Invoice on Delivery", body: "Digital, tax-compliant invoice handed over with every delivery — clean bookkeeping for B2B customers, always." },
                        ].map((f) => (
                            <div key={f.title} className="bg-white p-8 hover:bg-blue-50 transition-colors">
                                <div className="w-11 h-11 bg-slate-900 flex items-center justify-center rounded-sm mb-5">
                                    <f.icon className="w-5 h-5 text-yellow-400" />
                                </div>
                                <h3 className="font-heading font-bold text-xl tracking-tight text-slate-900">{f.title}</h3>
                                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="bg-slate-50 border-y border-slate-200 py-24" data-testid="how-it-works-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-end justify-between flex-wrap gap-4 mb-14">
                        <div>
                            <div className="text-xs font-mono-plex uppercase tracking-[0.2em] text-blue-700 mb-4">// How It Works</div>
                            <h2 className="font-heading font-black text-4xl md:text-5xl tracking-tighter text-slate-900">Four steps. One tank filled.</h2>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-4 gap-6">
                        {[
                            { n: "01", t: "Place Order", d: "Enter litres, address & preferred delivery slot." },
                            { n: "02", t: "Confirmation", d: "Our dispatch team confirms via call/SMS within 30 min." },
                            { n: "03", t: "Bowser Dispatch", d: "Metered bowser rolls out with trained operator." },
                            { n: "04", t: "Fill & Invoice", d: "Delivered, metered, invoiced. Pay Cash / UPI." },
                        ].map((s, i) => (
                            <div key={s.n} className="bg-white border border-slate-200 p-8 relative">
                                <div className="font-heading font-black text-6xl text-yellow-400/20 tab-num absolute top-2 right-4">{s.n}</div>
                                <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500 mb-4">Step {i + 1}</div>
                                <h3 className="font-heading font-bold text-xl tracking-tight text-slate-900">{s.t}</h3>
                                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* OUR FLEET - REAL BOWSER PHOTOS */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="fleet-section">
                <div className="grid lg:grid-cols-12 gap-10 items-center mb-14">
                    <div className="lg:col-span-5">
                        <div className="text-xs font-mono-plex uppercase tracking-[0.2em] text-blue-700 mb-4">// Our Fleet</div>
                        <h2 className="font-heading font-black text-4xl md:text-5xl tracking-tighter text-slate-900 leading-tight">
                            Purpose-built bowsers.<br /> Ready when you are.
                        </h2>
                        <p className="mt-6 text-slate-600 leading-relaxed">
                            Our Repos-certified mobile petrol pumps carry Bharat Petroleum diesel with tamper-proof dispensing, on-board fire safety, calibrated flow meters and full statutory labelling. Every truck is bowser-standard — not a converted tanker.
                        </p>
                        <ul className="mt-6 space-y-2 text-sm text-slate-700">
                            {[
                                "BPCL diesel · UN 1203 compliant",
                                "3-KL capacity per bowser",
                                "On-board Class-B fire extinguisher",
                                "Repos app enabled · Live tracking",
                            ].map((l) => (
                                <li key={l} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-blue-700 mt-0.5" /> {l}</li>
                            ))}
                        </ul>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="bg-white border border-slate-200 rounded-sm p-4">
                            <img src={BOWSER_SIDE} alt="Vaishali Fuels doorstep diesel delivery bowser — side profile" className="w-full h-auto object-contain bg-white" data-testid="fleet-hero-image" />
                        </div>
                    </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                        <img src={BOWSER_COLLAGE} alt="Vaishali Fuels bowser details — safety, meters, dispensing" className="w-full h-auto object-cover" data-testid="fleet-collage-image" />
                        <div className="p-6 border-t border-slate-200">
                            <h3 className="font-heading font-bold text-slate-900 tracking-tight">Every detail engineered.</h3>
                            <p className="mt-2 text-sm text-slate-600 leading-relaxed">Actual photos of our fleet — from the BPCL depot loading dock to safety cones, flow meters and fire suppression on every truck.</p>
                        </div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                        <img src={FLYER_ILLUSTRATION} alt="Doorstep diesel delivery — mobile petrol pump in your pocket" className="w-full h-auto object-cover" data-testid="fleet-flyer-image" />
                        <div className="p-6 border-t border-slate-200">
                            <h3 className="font-heading font-bold text-slate-900 tracking-tight">A petrol pump in your pocket.</h3>
                            <p className="mt-2 text-sm text-slate-600 leading-relaxed">Book from the website, track live, receive metered delivery at your gate. Your business never runs out of fuel again.</p>
                        </div>
                    </div>
                </div>
            </section>


            {/* PRICING */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="pricing-section">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="text-xs font-mono-plex uppercase tracking-[0.2em] text-blue-700 mb-4">// Transparent Pricing</div>
                        <h2 className="font-heading font-black text-4xl md:text-5xl tracking-tighter text-slate-900 leading-tight">
                            One rate. No games.<br /> Free delivery above 500L.
                        </h2>
                        <p className="mt-6 text-slate-600 leading-relaxed max-w-lg">
                            Our diesel rate is benchmarked daily against BPCL Vadodara depot rates. No hidden charges — you see the litre rate, delivery fee and total before confirming your order.
                        </p>
                        <ul className="mt-6 space-y-2 text-sm text-slate-700">
                            {[
                                "Minimum order: 250 litres",
                                "Flat delivery fee: ₹350 for orders under 500L",
                                "Free delivery on orders ≥ 500 litres",
                                "Cash / UPI on Delivery accepted",
                            ].map((l) => (
                                <li key={l} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-blue-700 mt-0.5" /> {l}</li>
                            ))}
                        </ul>
                    </div>
                    <div className="bg-slate-900 text-white p-10 relative overflow-hidden">
                        <div className="absolute inset-0 hero-grid opacity-30"></div>
                        <div className="relative">
                            <div className="font-mono-plex text-xs uppercase tracking-widest text-yellow-400 mb-4">Today's Rate · Vadodara</div>
                            <div className="flex items-baseline gap-3">
                                <div className="font-heading font-black text-5xl md:text-6xl text-white tracking-tighter">Live BPCL rate</div>
                            </div>
                            <p className="mt-4 text-slate-300 text-sm leading-relaxed max-w-sm">
                                We revise the per-litre rate every morning based on the BPCL Vadodara depot price. Enter your quantity in the order form to see today's live rate + your full quote before confirming.
                            </p>
                            <div className="mt-8 grid grid-cols-2 gap-4 border-t border-slate-700 pt-6">
                                <div>
                                    <div className="text-xs uppercase tracking-widest text-slate-400 font-mono-plex">Min Order</div>
                                    <div className="font-heading font-bold text-2xl text-white tab-num">250 L</div>
                                </div>
                                <div>
                                    <div className="text-xs uppercase tracking-widest text-slate-400 font-mono-plex">Delivery Fee</div>
                                    <div className="font-heading font-bold text-2xl text-white tab-num">₹350</div>
                                </div>
                                <div>
                                    <div className="text-xs uppercase tracking-widest text-slate-400 font-mono-plex">Free above</div>
                                    <div className="font-heading font-bold text-2xl text-yellow-400 tab-num">500 L</div>
                                </div>
                                <div>
                                    <div className="text-xs uppercase tracking-widest text-slate-400 font-mono-plex">Payment</div>
                                    <div className="font-heading font-bold text-2xl text-white">Cash / UPI</div>
                                </div>
                            </div>
                            <Link to="/order">
                                <Button className="mt-8 w-full h-12 rounded-sm bg-blue-700 hover:bg-blue-800 font-bold uppercase tracking-wider" data-testid="pricing-order-btn">
                                    Get My Quote <ArrowRight className="ml-2 w-4 h-4" />
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* WHO WE SERVE */}
            <section className="bg-slate-900 text-white py-24" data-testid="industries-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-12">
                        <div>
                            <div className="text-xs font-mono-plex uppercase tracking-[0.2em] text-yellow-400 mb-4">// Industries We Serve</div>
                            <h2 className="font-heading font-black text-4xl md:text-5xl tracking-tighter leading-tight">
                                Wherever a diesel engine runs, we deliver.
                            </h2>
                            <p className="mt-6 text-slate-400 leading-relaxed max-w-lg">
                                From continuous-process factories in Halol to farm pumps in Padra villages — our bowsers, operators and dispatch team are built for Gujarat's diverse fuel demand.
                            </p>
                            <div className="mt-8 grid grid-cols-2 gap-y-3 gap-x-6 text-sm">
                                {["Manufacturing", "Construction", "Cold Storage", "Data Centers", "Transport Fleets", "Agriculture", "Hospitals", "Real Estate"].map((i) => (
                                    <div key={i} className="flex items-center gap-2"><Truck className="w-4 h-4 text-yellow-400" /> {i}</div>
                                ))}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 gap-4">
                            <img src={LEAFLET_ILLUSTRATION} alt="Doorstep diesel delivery illustration" className="w-full h-72 object-cover object-center bg-white" />
                            <div className="bg-blue-700 p-6">
                                <div className="font-heading font-black text-3xl tracking-tighter">Trusted by 200+ businesses across Gujarat</div>
                                <div className="mt-2 text-blue-100 text-sm">Recurring supply contracts · Monthly billing · Priority slotting</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* COVERAGE */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="coverage-section">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
                    <div>
                        <div className="text-xs font-mono-plex uppercase tracking-[0.2em] text-blue-700 mb-4">// Service Coverage</div>
                        <h2 className="font-heading font-black text-4xl md:text-5xl tracking-tighter text-slate-900">Currently active in these zones.</h2>
                    </div>
                    <Link to="/coverage" className="text-sm font-bold uppercase tracking-widest text-blue-700 hover:underline">View all zones →</Link>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
                    {(pricing?.service_areas || ["Vadodara", "Ankleshwar", "Bharuch", "Halol", "Padra", "Karjan", "Savli", "Waghodia"]).map((z) => (
                        <div key={z} className="bg-white p-6 flex items-center gap-3 hover:bg-blue-50 transition-colors">
                            <MapPin className="w-5 h-5 text-blue-700" />
                            <span className="font-heading font-bold text-slate-900">{z}</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="bg-blue-700 text-white" data-testid="footer-cta-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 flex flex-col md:flex-row items-center justify-between gap-8">
                    <div>
                        <h3 className="font-heading font-black text-3xl md:text-4xl tracking-tighter">Running low on diesel?</h3>
                        <p className="mt-2 text-blue-100">Book now. Delivered in 4 hours across Vadodara.</p>
                    </div>
                    <div className="flex gap-3">
                        <Link to="/order">
                            <Button className="bg-white text-blue-800 hover:bg-slate-100 rounded-sm h-12 px-8 font-bold uppercase tracking-wider" data-testid="cta-order-btn">
                                Place Order
                            </Button>
                        </Link>
                        <a href="tel:+919512477777" className="inline-flex items-center gap-2 border-2 border-white h-12 px-6 rounded-sm text-sm font-bold uppercase tracking-wider hover:bg-white hover:text-blue-800 transition-colors">
                            <PhoneCall className="w-4 h-4" /> +91 95124 77777
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
